/**
 * Legacy category / subcategory / type aliases → canonical catalog keys.
 */

/** Non-canonical keys → canonical tree key */
export const CATEGORY_KEY_ALIASES = {
  // Richer tree is office-business-school; office-school is the legacy/slim twin
  'office-school': 'office-business-school',
  food: 'food-beverages',
  supermarket: 'food-beverages',
  poultry: 'food-beverages',
  'alimentation-animaux': 'pets',
};

/** French (and a few POS) display names → canonical keys */
export const CATEGORY_NAME_ALIASES = {
  Épicerie: 'food-beverages',
  Hygiène: 'health-beauty',
  Boissons: 'food-beverages',
  'Produits laitiers': 'food-beverages',
  Boulangerie: 'boulangerie',
  Entretien: 'home-garden',
  Électronique: 'electronics',
  Vêtements: 'clothing',
  Bureau: 'office-business-school',
  Volaille: 'food-beverages',
};

/**
 * Subcategory remaps keyed by canonical category after category aliasing.
 * Used when the source category tree used different subcategory keys.
 */
export const SUBCATEGORY_KEY_ALIASES = {
  'office-business-school': {
    stationery: 'office-supplies',
    'office-equipment': 'business-equipment',
  },
};

/**
 * Type remaps keyed by canonical category (optionally scoped by target subcategory).
 * Format: { [canonicalCategory]: { [fromType]: toType | { [toSub]: toType } } }
 */
export const TYPE_KEY_ALIASES = {
  'office-business-school': {
    planners: 'agendas',
    textbooks: 'books',
  },
};

/**
 * Resolve any category value (key, alias, or FR/AR label) toward a canonical key.
 * Does not invent keys — returns trimmed input when unknown.
 */
export function canonicalizeCategoryKey(value, categories = null) {
  if (!value || typeof value !== 'string') return '';
  const trimmed = value.trim();
  if (!trimmed) return '';

  if (CATEGORY_KEY_ALIASES[trimmed]) {
    return CATEGORY_KEY_ALIASES[trimmed];
  }

  if (categories && categories[trimmed]) {
    return trimmed;
  }

  if (CATEGORY_NAME_ALIASES[trimmed]) {
    return CATEGORY_NAME_ALIASES[trimmed];
  }

  if (categories) {
    const byLabel = Object.entries(categories).find(
      ([, cat]) => cat?.name === trimmed || cat?.nameAr === trimmed
    );
    if (byLabel) return byLabel[0];
  }

  return trimmed;
}

function isValidSubKey(categories, categoryKey, subcategoryKey) {
  return Boolean(
    categoryKey &&
      subcategoryKey &&
      categories?.[categoryKey]?.subcategories?.[subcategoryKey]
  );
}

function typeExists(categories, categoryKey, subcategoryKey, typeKey) {
  if (!typeKey || !isValidSubKey(categories, categoryKey, subcategoryKey)) return false;
  const types = categories[categoryKey].subcategories[subcategoryKey].types || [];
  return types.some((entry) => {
    if (typeof entry === 'string') return entry === typeKey;
    return entry?.key === typeKey;
  });
}

/**
 * Canonicalize category + subcategory + type together (with office twin remaps).
 *
 * @returns {{
 *   category: string,
 *   subcategory: string,
 *   type: string,
 *   subcategoryRemapped: boolean,
 *   subcategoryInvalid: boolean,
 *   hadExplicitSubcategory: boolean,
 * }}
 */
export function canonicalizeCatalogPath(
  { category = '', subcategory = '', type = '' } = {},
  categories = null
) {
  const rawCategory = typeof category === 'string' ? category.trim() : '';
  const rawSub = typeof subcategory === 'string' ? subcategory.trim() : '';
  const rawType = typeof type === 'string' ? type.trim() : '';
  const hadExplicitSubcategory = Boolean(rawSub);

  const canonicalCategory = canonicalizeCategoryKey(rawCategory, categories) || '';
  if (!canonicalCategory) {
    return {
      category: '',
      subcategory: '',
      type: '',
      subcategoryRemapped: false,
      subcategoryInvalid: false,
      hadExplicitSubcategory,
    };
  }

  let nextSub = rawSub;
  let subcategoryRemapped = false;

  const subAliases = SUBCATEGORY_KEY_ALIASES[canonicalCategory] || {};
  if (nextSub && subAliases[nextSub]) {
    nextSub = subAliases[nextSub];
    subcategoryRemapped = true;
  }

  // If still invalid under canonical category, try resolving by FR/AR label
  if (
    categories &&
    nextSub &&
    !isValidSubKey(categories, canonicalCategory, nextSub)
  ) {
    const subs = categories[canonicalCategory]?.subcategories || {};
    const byLabel = Object.entries(subs).find(
      ([, sub]) => sub?.name === nextSub || sub?.nameAr === nextSub
    );
    if (byLabel) {
      nextSub = byLabel[0];
      subcategoryRemapped = true;
    }
  }

  const subcategoryInvalid = Boolean(
    hadExplicitSubcategory &&
      nextSub &&
      categories &&
      !isValidSubKey(categories, canonicalCategory, nextSub)
  );

  if (subcategoryInvalid) {
    return {
      category: canonicalCategory,
      subcategory: '',
      type: '',
      subcategoryRemapped,
      subcategoryInvalid: true,
      hadExplicitSubcategory,
    };
  }

  let nextType = rawType;
  const typeAliases = TYPE_KEY_ALIASES[canonicalCategory] || {};
  if (nextType && typeAliases[nextType]) {
    nextType = typeAliases[nextType];
  }

  if (
    nextType &&
    nextSub &&
    categories &&
    !typeExists(categories, canonicalCategory, nextSub, nextType)
  ) {
    // Fail closed on type only — keep subcategory
    nextType = '';
  }

  return {
    category: canonicalCategory,
    subcategory: nextSub,
    type: nextType,
    subcategoryRemapped,
    subcategoryInvalid: false,
    hadExplicitSubcategory,
  };
}
